/* eslint-disable karda/no-hex-color-literals --
 * This module emits a self-contained print-HTML fragment rendered offscreen
 * by html2canvas, not a MUI component; theme.palette / design tokens aren't in
 * scope, so literal hex in the print stylesheet is intentional and correct. */
/**
 * Builds a self-contained, print-styled HTML fragment for a completed IA
 * compliance run and exports it to PDF client-side (html2pdf.js, the same
 * dynamic-import idiom as TranscriptViewer). The run detail is already loaded
 * in the authorized station_admin's browser, so exporting adds no new data
 * egress — the artifact is generated on demand and never stored server-side.
 *
 * Purpose: give an investigator a document to attach to a Versaterm IAPro
 * case (IAPro lets a user attach any document as a permanent part of a case).
 * Each report block prints its KARDA Report ID so the investigator can
 * correlate the artifact with the corresponding RMS/IAPro case record.
 *
 * The builder is a pure function (no DOM, no React) so it unit-tests without
 * rendering; it escapes every dynamic value because the output is injected via
 * innerHTML for html2canvas.
 */
import type {
  ComplianceResult,
  ComplianceRun,
} from "@/features/stationAdmin/api/iaComplianceApi";
import {
  SEVERITY_LABEL,
  formatShortDateTime,
  normalizeFindings,
  resultVerdictChip,
} from "./complianceView";

export interface ComplianceReportInput {
  run: ComplianceRun;
  results: ComplianceResult[];
  stationName?: string;
  /** Injectable for deterministic tests; defaults to now at call time. */
  generatedAt?: Date;
}

/** HTML-escape a dynamic value before it goes into the innerHTML string. */
export function escapeHtml(value: unknown): string {
  // & first so the entities added below aren't re-escaped.
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

const SEVERITY_COLOR: Record<string, string> = {
  violation: "#b3261e",
  warning: "#8a6d00",
  info: "#1f6feb",
};

function metaRow(label: string, value: string): string {
  return `<tr><th>${escapeHtml(label)}</th><td>${escapeHtml(value)}</td></tr>`;
}

function findingBlock(finding: {
  severity: "violation" | "warning" | "info";
  policyReference: string;
  requirement: string;
  evidence: string;
  explanation: string;
}): string {
  const color = SEVERITY_COLOR[finding.severity] ?? SEVERITY_COLOR.warning;
  const line = (label: string, text: string) =>
    text
      ? `<div class="fld"><span class="lbl">${escapeHtml(label)}</span> ${escapeHtml(text)}</div>`
      : "";
  return `
    <div class="finding">
      <div class="sev" style="color:${color};border-color:${color}">${escapeHtml(
        SEVERITY_LABEL[finding.severity],
      )}</div>
      ${line("Policy", finding.policyReference)}
      ${line("Requirement", finding.requirement)}
      ${line("Evidence", finding.evidence)}
      ${line("Assessment", finding.explanation)}
    </div>`;
}

function resultBlock(result: ComplianceResult): string {
  const verdict = resultVerdictChip(result);
  const heading = result.title || `Report ${result.reportId}`;

  if (result.status === "error") {
    return `
      <section class="report">
        <h2>${escapeHtml(heading)}</h2>
        <div class="idline">KARDA Report ID: <code>${escapeHtml(result.reportId)}</code></div>
        <div class="verdict warn">${escapeHtml(verdict.label)}</div>
        <p class="err">${escapeHtml(result.error || "This report could not be checked.")}</p>
      </section>`;
  }

  const findings = normalizeFindings(result.findings);
  const findingsHtml =
    findings.length > 0
      ? findings.map(findingBlock).join("")
      : `<p class="none">No findings for this report.</p>`;
  const confidence = result.confidence
    ? `<span class="conf">${escapeHtml(result.confidence)} confidence</span>`
    : "";
  const summary = result.summary
    ? `<p class="summary">${escapeHtml(result.summary)}</p>`
    : "";

  return `
    <section class="report">
      <h2>${escapeHtml(heading)}</h2>
      <div class="idline">KARDA Report ID: <code>${escapeHtml(result.reportId)}</code></div>
      <div class="verdict">${escapeHtml(verdict.label)} ${confidence}</div>
      ${summary}
      ${findingsHtml}
    </section>`;
}

/**
 * Render a compliance run to a self-contained HTML fragment. Every stylesheet
 * selector is rooted at `.ia-compliance-print`, so attaching the fragment for
 * html2pdf rendering cannot restyle the host application. Pure and
 * deterministic given `generatedAt`.
 */
export function buildComplianceReportHtml(input: ComplianceReportInput): string {
  const { run, results, stationName } = input;
  const generatedAt = input.generatedAt ?? new Date();

  const policies =
    run.policyDocNames.length > 0
      ? run.policyDocNames.join(", ")
      : "(none recorded)";
  const runDate = run.completedAt || run.createdAt;

  const metaRows = [
    metaRow("Run ID", run.runId),
    metaRow("Run date", formatShortDateTime(runDate)),
    metaRow("Status", run.status),
    metaRow("Policies checked", policies),
    metaRow("Reports checked", `${run.checkedCount} of ${run.reportCount}`),
    metaRow("Reports flagged", String(run.violationCount)),
    metaRow("Checks failed", String(run.errorCount)),
  ].join("");

  const body =
    results.length > 0
      ? results.map(resultBlock).join("")
      : `<p class="none">No per-report results were recorded for this check.</p>`;

  return `<div class="ia-compliance-print"><style>
    .ia-compliance-print { box-sizing: border-box; font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #1a1a2e; margin: 0; padding: 32px 36px; font-size: 12px; line-height: 1.5; }
    .ia-compliance-print *, .ia-compliance-print *::before, .ia-compliance-print *::after { box-sizing: border-box; }
    .ia-compliance-print h1 { font-size: 20px; margin: 0 0 2px; }
    .ia-compliance-print .station { color: #555; font-size: 13px; margin: 0 0 14px; }
    .ia-compliance-print .advisory { border: 1px solid #8a6d00; background: #fdf6e3; color: #6b5400; border-radius: 6px; padding: 8px 12px; font-size: 11px; margin: 0 0 16px; }
    .ia-compliance-print table.meta { border-collapse: collapse; width: 100%; margin: 0 0 20px; }
    .ia-compliance-print table.meta th { text-align: left; width: 150px; color: #555; font-weight: 600; vertical-align: top; padding: 3px 8px 3px 0; }
    .ia-compliance-print table.meta td { padding: 3px 0; }
    .ia-compliance-print section.report { border: 1px solid #d9dce3; border-radius: 8px; padding: 14px 16px; margin: 0 0 12px; page-break-inside: avoid; }
    .ia-compliance-print section.report h2 { font-size: 14px; margin: 0 0 4px; }
    .ia-compliance-print .idline { color: #555; font-size: 11px; margin: 0 0 6px; }
    .ia-compliance-print .idline code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; background: #f2f3f5; padding: 1px 5px; border-radius: 4px; }
    .ia-compliance-print .verdict { font-weight: 700; margin: 0 0 8px; }
    .ia-compliance-print .verdict.warn { color: #8a6d00; }
    .ia-compliance-print .conf { font-weight: 400; color: #555; font-size: 11px; }
    .ia-compliance-print .summary { margin: 0 0 10px; white-space: pre-wrap; }
    .ia-compliance-print .finding { border-left: 3px solid #d9dce3; padding: 6px 0 6px 12px; margin: 0 0 10px; }
    .ia-compliance-print .sev { display: inline-block; border: 1px solid; border-radius: 999px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; padding: 1px 8px; margin: 0 0 5px; }
    .ia-compliance-print .fld { margin: 2px 0; }
    .ia-compliance-print .fld .lbl { font-weight: 600; color: #555; }
    .ia-compliance-print .none { color: #777; font-style: italic; }
    .ia-compliance-print .err { color: #b3261e; }
    .ia-compliance-print footer { margin-top: 20px; padding-top: 10px; border-top: 1px solid #e3e7ed; color: #888; font-size: 10px; }
  </style>
    <h1>Internal Affairs — Report Compliance Check</h1>
    ${stationName ? `<p class="station">${escapeHtml(stationName)}</p>` : ""}
    <div class="advisory">AI-generated — advisory only. These findings are produced by an automated policy check and must be verified against the policy text before any action is taken.</div>
    <table class="meta">${metaRows}</table>
    ${body}
    <footer>Generated by KARDA on ${escapeHtml(formatShortDateTime(generatedAt.toISOString()))}</footer>
  </div>`;
}

/** Filename for the exported artifact: traceable by run id + date. */
export function complianceReportFilename(
  run: ComplianceRun,
  generatedAt: Date = new Date(),
): string {
  const date = generatedAt.toISOString().split("T")[0];
  return `ia-compliance-${run.runId}-${date}.pdf`;
}

/**
 * Build the HTML and hand it to html2pdf.js for a client-side PDF download.
 * Throws on failure — the caller owns toast/error handling (TranscriptViewer
 * precedent). DOM-touching + dynamic import, so exercised via the app rather
 * than unit tests; the HTML it renders is covered by buildComplianceReportHtml.
 */
export async function exportComplianceRunPdf(
  input: ComplianceReportInput,
): Promise<void> {
  const generatedAt = input.generatedAt ?? new Date();
  const html = buildComplianceReportHtml({ ...input, generatedAt });
  const html2pdf = (await import("html2pdf.js")).default;

  const container = document.createElement("div");
  container.innerHTML = html;

  await html2pdf()
    .set({
      margin: 0,
      filename: complianceReportFilename(input.run, generatedAt),
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    })
    .from(container)
    .save();
}
